/* eslint-disable indent */
import './OrderSummary.scss';

import React, { Suspense, lazy } from 'react';
import classNames from 'classnames';
import { useFormContext } from 'react-hook-form';
import {
  getStoreOrderSummaryEstDeliveryTooltip,
  getStoreCheckoutDeliveryShipTypePillStyles,
  STORE_CHECKOUT_DELIVERY_SHIP_TYPE_OPTIONS,
  STORE_LAUNCH_DISABLED_TOOLTIP_INNER_STYLE,
  STORE_ORDER_SUMMARY_EST_DELIVERY_BY_LABEL,
} from '../../StoreCheckout/constants/storeCheckout.constants';
import {
  formatStoreOrderSummaryCloseDate,
  getStoreOrderSummaryProductImage,
  groupStoreOrderSummaryProducts,
} from '../../StoreCheckout/helpers/storeOrderSummary.utils';
import {
  MOBILE_TAB_DESKTOP_SMALL_VIEWPORT,
  REGULAR_ORDER_SUMMARY_TEXT,
  REGULAR_ORDER_SUMMARY_TEXT_MOBILE,
} from '@/constants/Order';

import Button from '@/components/Button/Button';
import type { ICartItemDetail } from '@/common/interfaces/cart.interface';
import type { IOrderSummaryProps } from '@/common/interfaces/order.interface';
import Icon from '@/components/Icon/Icon';
import ImageWrapper from '@/components/ImageWrapper';
import PaymentLogos from '@/components/PaymentLogos/PaymentLogos';
import Summary from '@/components/Summary/Summary';
import { Text } from '@/components/Text/Text';
import { ToolTip } from '@/components/Tooltip/Tooltip';
import { canClientPayLater } from '@/utilities/FeatureUtils';
import { getTooltipTitle } from '@/utilities/CreateOrderUtils.misc';
import { isMobileView } from '@/utilities/CommonUtils.misc';
import { useStyleClass } from '@/hooks/useStyleClass';
import useViewport from '@/common/context/ViewPort/useViewPort';
import { validateZipCode } from '@/utilities/FormUtils';
import { PROMO_CODE_TYPES } from '@/constants/Common';

const OrderCheckout = lazy(
  () => import('@/modules/Orders/OrderDetail/OrderCheckout/OrderCheckout')
);

interface IOrderSummaryDesignGroup {
  name: string;
  pricingModel?: string;
  items: ICartItemDetail[];
}

/** OrderSummary is the component that has summary of invoice and shipping that the user have entered */
const OrderSummary: React.FC<IOrderSummaryProps> = ({
  items,
  shippingFormValues,
  sizeFormValues,
  enablePlaceOrder = false,
  priceZeroCartItem = false,
  showOrderSummary = false,
  showShippingDetails = false,
  onSubmit,
  estimatedDeliveryDate,
  promoFocus,
  promoCodeData,
  promoApplied,
  promoCodeError,
  setPromoCodeError,
  onPromoApplyClick,
  onPromoRemoveClick,
  showConfetti,
  showBraintreePaymentScreen,
  continueToPay,
  isDropInInitialized,
  isStoreCheckout = false,
  isBraintreeFormValid,
  isPayLater,
  isBraintreeEnabled,
  selectedPaymentOption = 'checkout',
  designGroups = [],
  storeCloseDate = '',
  storeLaunchTooltipTitle = '',
  onPreviewStore,
  canPreviewStore = false,
  storeEstimatedDeliveryRange = '',
  selectedDeliveryShipType = '',
  onDeliveryShipTypeSelect,
}) => {
  const formattedCloseDate = formatStoreOrderSummaryCloseDate(storeCloseDate);
  const formattedDeliveryDateRange = storeEstimatedDeliveryRange || 'TBD';
  const resolvedDeliveryShipType = selectedDeliveryShipType;
  const styleClass = useStyleClass('order-summary', true);
  const isMobile = isMobileView(styleClass);
  const screen = useViewport(true);
  const tabMobileScreen = MOBILE_TAB_DESKTOP_SMALL_VIEWPORT.includes(screen);
  const hasAccount: boolean = canClientPayLater();
  const { watch: watchSummary, control: summaryFormControl } = useFormContext();
  const {
    subTotal,
    shipping,
    salesTax,
    total,
    discountedTotal,
    isSalesTaxSuccess,
    shipTypeKey,
    guarantee,
  } = watchSummary();
  const footerContentClassName = classNames({
    [`${styleClass}__logo-container`]: !tabMobileScreen,
    [`${styleClass}__logo-container_mob`]: tabMobileScreen,
  });

  const orderCheckoutData = {
    address: {
      shippingName: shippingFormValues.shippingName || '',
      street1: shippingFormValues.street1 || '',
      street2: shippingFormValues.street2 || '',
      city: shippingFormValues.city || '',
      state: shippingFormValues.state?.label || '',
      zipCode: shippingFormValues.shippingZipCode || '',
    },
    orderStatus: 'other',
    dueDate: estimatedDeliveryDate,
  };

  const isSummaryContainsRejectedItems = items.some(
    (item) =>
      item.designRejectionInfo?.isDesignSelfBlockedForCheckout ||
      item.designRejectionInfo?.isDesignLicensingRejected
  );

  return (
    <div
      className={`${styleClass} ${showOrderSummary && 'expanded order-summary-card'} ${
        showShippingDetails && 'ship-detail-expanded'
      } ${isStoreCheckout ? 'store-checkout-summary' : ''}`}
      style={isStoreCheckout && !tabMobileScreen ? { borderRadius: '0 0 16px 16px' } : undefined}
    >
      {showOrderSummary ? (
        <>
          {isStoreCheckout ? (
            <>
              {/* Scrollable Content */}
              <div
                className="store-checkout-scrollable"
                style={{
                  flex: '1 1 auto',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  maxHeight: tabMobileScreen ? undefined : 'calc(95vh - 250px)',
                  width: '100%',
                  boxSizing: 'border-box',
                  paddingLeft: '0',
                  paddingRight: '0',
                  paddingBottom: isMobile ? '160px' : undefined,
                }}
              >
                {/* Date Cards */}
                <div
                  style={{
                    display: 'flex',
                    gap: '12px',
                    marginBottom: '20px',
                    width: '100%',
                    boxSizing: 'border-box',
                    paddingTop: '16px',
                  }}
                >
                  {/* Store Closes On Card */}
                  <div
                    style={{
                      flex: 1,
                      padding: '12px',
                      background: '#FFFFFF',
                      border: '1px solid #E5E7EB',
                      borderRadius: '12px',
                      minWidth: 0,
                      boxSizing: 'border-box',
                    }}
                  >
                    <div
                      style={{
                        fontFamily: 'SF UI Text, system-ui, -apple-system, sans-serif',
                        fontSize: '12px',
                        fontWeight: 400,
                        color: '#9CA3AF',
                        marginBottom: '8px',
                      }}
                    >
                      Store Closes On
                    </div>
                    <div
                      style={{
                        fontFamily: 'SF UI Text, system-ui, -apple-system, sans-serif',
                        fontSize: '18px',
                        fontWeight: 700,
                        color: '#474B6D',
                        marginBottom: '4px',
                      }}
                    >
                      {formattedCloseDate}
                    </div>
                  </div>

                  {/* Delivery By Card */}
                  <div
                    style={{
                      flex: 1,
                      padding: '12px',
                      background: '#FFFFFF',
                      border: '1px solid #E5E7EB',
                      borderRadius: '12px',
                      minWidth: 0,
                      boxSizing: 'border-box',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        marginBottom: '8px',
                      }}
                    >
                      <div
                        style={{
                          fontFamily: 'SF UI Text, system-ui, -apple-system, sans-serif',
                          fontSize: '12px',
                          fontWeight: 400,
                          color: '#9CA3AF',
                        }}
                      >
                        {STORE_ORDER_SUMMARY_EST_DELIVERY_BY_LABEL}
                      </div>
                      <ToolTip
                        title={getStoreOrderSummaryEstDeliveryTooltip(formattedCloseDate)}
                        placement="top"
                      >
                        <button
                          type="button"
                          aria-label="Estimated delivery information"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: 0,
                            margin: 0,
                            border: 'none',
                            background: 'transparent',
                            cursor: 'pointer',
                            lineHeight: 0,
                          }}
                        >
                          <Icon source="/src/assets/svg/info_icon.svg" width="12px" height="12px" />
                        </button>
                      </ToolTip>
                    </div>
                    <div
                      style={{
                        fontFamily: 'SF UI Text, system-ui, -apple-system, sans-serif',
                        fontSize: '18px',
                        fontWeight: 700,
                        color: '#474B6D',
                        marginBottom: '4px',
                      }}
                    >
                      {formattedDeliveryDateRange}
                    </div>
                  </div>
                </div>

                {/* Design Groups - Dynamic */}
                <div className={`${styleClass}__design-groups-container`}>
                  {(designGroups as IOrderSummaryDesignGroup[]).map((group, groupIndex: number) => (
                    <div
                      key={groupIndex}
                      className={`${styleClass}__design-group-item`}
                      style={{ marginBottom: groupIndex < designGroups.length - 1 ? '16px' : '0' }}
                    >
                      <div
                        style={{
                          fontFamily: 'SF UI Text, system-ui, -apple-system, sans-serif',
                          fontSize: '14px',
                          fontWeight: 400,
                          color: '#9CA3AF',
                          marginBottom: '8px',
                        }}
                      >
                        {group.name}
                      </div>
                      {(group.pricingModel === 'drops' || group.pricingModel === 'same') && (
                        <div
                          style={{
                            fontFamily: 'SF UI Text, system-ui, -apple-system, sans-serif',
                            fontSize: '14px',
                            fontWeight: 600,
                            color: '#474B6D',
                            marginBottom: '12px',
                          }}
                        >
                          {group.pricingModel === 'drops'
                            ? 'Price Drops as More People Order'
                            : 'Price Stays the Same'}
                        </div>
                      )}
                      {/* Product Cards Container */}
                      <div className={`${styleClass}__product-cards-container`}>
                        {groupStoreOrderSummaryProducts(group.items).map((productGroup) => {
                          const item = productGroup.primaryItem;

                          return (
                            <div
                              key={item.apparelStyleCode || String(item.id)}
                              className={`${styleClass}__product-card`}
                            >
                              <div className={`${styleClass}__product-card-image`}>
                                <img src={getStoreOrderSummaryProductImage(item)} alt="Product" />
                              </div>
                              <div className={`${styleClass}__product-card-swatches`}>
                                {productGroup.colors.map((colorInfo) => (
                                  <div
                                    key={`${item.apparelStyleCode}-${
                                      colorInfo.color || colorInfo.hex
                                    }`}
                                    className={`${styleClass}__product-card-swatch`}
                                    style={{ background: colorInfo.hex }}
                                    title={colorInfo.color || undefined}
                                  />
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Payment and Shipping Info */}
                <div style={{ marginTop: '24px', marginBottom: '24px' }}>
                  {/* Payment Option - Only show if selected */}
                  {selectedPaymentOption && (
                    <div style={{ marginBottom: '16px' }}>
                      <span
                        style={{
                          fontFamily: 'SF UI Text, system-ui, -apple-system, sans-serif',
                          fontSize: '14px',
                          fontWeight: 400,
                          color: '#9CA3AF',
                        }}
                      >
                        Payment Option:{' '}
                      </span>
                      <span
                        style={{
                          fontFamily: 'SF UI Text, system-ui, -apple-system, sans-serif',
                          fontSize: '14px',
                          fontWeight: 600,
                          color: '#474B6D',
                        }}
                      >
                        {selectedPaymentOption === 'checkout' ? 'Pay via Checkout' : 'Pay Later'}
                      </span>
                    </div>
                  )}

                  {/* Shipping To - Only show if shipping option is selected */}
                  {shippingFormValues.shippingOption &&
                    (shippingFormValues.shippingOption === 'one-place' ? (
                      <>
                        <div
                          style={{
                            marginBottom: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                          }}
                        >
                          <span
                            style={{
                              fontFamily: 'SF UI Text, system-ui, -apple-system, sans-serif',
                              fontSize: '14px',
                              fontWeight: 400,
                              color: '#9CA3AF',
                            }}
                          >
                            Shipping To
                          </span>
                          {STORE_CHECKOUT_DELIVERY_SHIP_TYPE_OPTIONS.map((option) => {
                            const isSelected = resolvedDeliveryShipType === option.shipTypeKey;
                            const optionStyles = getStoreCheckoutDeliveryShipTypePillStyles(
                              option.shipTypeKey,
                              isSelected
                            );

                            return (
                              <button
                                key={option.shipTypeKey}
                                type="button"
                                onClick={() => onDeliveryShipTypeSelect?.(option.shipTypeKey)}
                                className={`${styleClass}__shipping-pill`}
                                style={{
                                  border: optionStyles.border,
                                  background: optionStyles.background,
                                  color: optionStyles.color,
                                  cursor: onDeliveryShipTypeSelect ? 'pointer' : 'default',
                                }}
                              >
                                {option.label}
                              </button>
                            );
                          })}
                        </div>

                        {/* Shipping Address */}
                        <div
                          style={{
                            fontFamily: 'SF UI Text, system-ui, -apple-system, sans-serif',
                            fontSize: '14px',
                            fontWeight: 600,
                            color: '#474B6D',
                            lineHeight: '1.6',
                          }}
                        >
                          <div>
                            {shippingFormValues.shippingName || shippingFormValues.street1 || ''}
                          </div>
                          <div>
                            {shippingFormValues.street1 || ''}
                            {shippingFormValues.street2 ? `, ${shippingFormValues.street2}` : ''}
                          </div>
                          <div>
                            {shippingFormValues.city || ''}
                            {shippingFormValues.city && ', '}
                            {shippingFormValues.state?.label || ''}{' '}
                            {shippingFormValues.shippingZipCode || ''}
                          </div>
                        </div>
                      </>
                    ) : (
                      <div>
                        <span
                          style={{
                            fontFamily: 'SF UI Text, system-ui, -apple-system, sans-serif',
                            fontSize: '14px',
                            fontWeight: 400,
                            color: '#9CA3AF',
                          }}
                        >
                          Shipping To:{' '}
                        </span>
                        <span
                          style={{
                            fontFamily: 'SF UI Text, system-ui, -apple-system, sans-serif',
                            fontSize: '14px',
                            fontWeight: 600,
                            color: '#474B6D',
                          }}
                        >
                          Customer&apos;s Address
                        </span>
                      </div>
                    ))}
                </div>
              </div>
            </>
          ) : (
            <div className="order-summary-delivery-title">
              <Icon source="/src/assets/svg/truck_green.svg" height="14px" width="16px" /> Order
              today and get it by {estimatedDeliveryDate}.
            </div>
          )}
          {showShippingDetails && !isStoreCheckout && (
            <div className={`${styleClass}__ship-container`}>
              <Suspense>
                <OrderCheckout
                  {...orderCheckoutData}
                  shippingType={shipTypeKey}
                  hideEstimateDelivery
                />
              </Suspense>
            </div>
          )}

          {!isStoreCheckout && (
            <>
              <div className={`${styleClass}__header order-summary-header`}>
                <Text as="div" color="#474B6D" className={`${styleClass}__header__title`}>
                  Order Summary
                </Text>
              </div>
              <div className={`${styleClass}__content summary-content`}>
                <div className={`${styleClass}__item-container summary-item-container`}>
                  <Summary
                    summaryItems={items.map((item) => ({
                      imageSrc: item.designFrontImage,
                      quantity: (sizeFormValues?.sizes?.[item.id]?.total ?? 0) as number,
                      totalPrice: (sizeFormValues?.sizes?.[item.id]?.perItemTotal ?? 0) as number,
                      pricePerUnit: (sizeFormValues?.sizes?.[item.id]?.pricePerUnit ?? 0) as number,
                      discountedPrice: (sizeFormValues?.sizes?.[item.id]?.strikedPricePerUnit ??
                        0) as number,
                      isItemRejected:
                        item.designRejectionInfo?.isDesignSelfBlockedForCheckout ||
                        item.designRejectionInfo?.isDesignLicensingRejected,
                      ...item,
                    }))}
                    salesTax={
                      isSalesTaxSuccess && salesTax > 0 && !isSummaryContainsRejectedItems
                        ? salesTax
                        : 0
                    }
                    shipping={isSummaryContainsRejectedItems ? 0 : shipping}
                    subTotal={subTotal}
                    total={total}
                    discountedTotal={discountedTotal}
                    shipTypeKey={shipTypeKey}
                    showSalesTax={
                      salesTax > 0 || !validateZipCode(shippingFormValues.shippingZipCode)
                    }
                    isItemRejected={isSummaryContainsRejectedItems}
                    promoFocus={promoFocus}
                    promoCodeData={promoCodeData}
                    promoApplied={promoApplied}
                    onPromoApplyClick={onPromoApplyClick}
                    onPromoRemoveClick={onPromoRemoveClick}
                    promoCodeError={promoCodeError}
                    setPromoCodeError={setPromoCodeError}
                    showConfetti={showConfetti}
                    showGuarantee={
                      promoCodeData?.type === PROMO_CODE_TYPES.noGuaranteeFee ? 'FREE' : guarantee
                    }
                    summaryFormControl={summaryFormControl}
                  />
                </div>
              </div>
            </>
          )}
        </>
      ) : (
        <>
          <div className={`${styleClass}__header`}>
            <Text as="div" className={`${styleClass}__header__title`} color="#191960">
              Review Your Order
            </Text>
            <Text as="div" className={`${styleClass}__header__subtitle`}>
              Items will start showing up here once you add quantity and sizes.
            </Text>
          </div>
          <div className={`${styleClass}__content`}>
            <ImageWrapper
              imgUrl="/src/assets/png/shopping_bag.png"
              size="large"
              height={145}
              width={165}
              className={`${styleClass}__img-container`}
            />
          </div>
        </>
      )}

      {showBraintreePaymentScreen && !isStoreCheckout && (
        <div className={`${styleClass}__footer checkout-footer`}>
          <Button
            className={`${styleClass}__footer__button`}
            iconClassName="order-summary__footer__icon"
            labelClassName="order-summary__footer__label"
            iconOnLeftSrc="/src/assets/svg/lock.svg"
            iconOnRightSrc="/src/assets/svg/double_arrow.svg"
            disabled={!enablePlaceOrder || !isBraintreeFormValid || !isDropInInitialized}
            onClick={onSubmit}
          >
            {isMobile ? REGULAR_ORDER_SUMMARY_TEXT_MOBILE : REGULAR_ORDER_SUMMARY_TEXT}
          </Button>
          <div className={`${footerContentClassName}`}>
            <PaymentLogos
              className="order-summary-payment-logos"
              showVenmoLogo
              showChequeLogo={false}
            />
          </div>
        </div>
      )}

      {isBraintreeEnabled && !showBraintreePaymentScreen && !isPayLater && !isStoreCheckout && (
        <div className={`${styleClass}__footer checkout-footer`}>
          <ToolTip
            title={getTooltipTitle(
              priceZeroCartItem,
              enablePlaceOrder,
              isSummaryContainsRejectedItems
            )}
            overlayInnerStyle={{ boxShadow: '0px 4px 15px #8588AC26', color: '#E86868' }}
          >
            <Button
              className={`${styleClass}__footer__button`}
              iconClassName="order-summary__footer__icon"
              labelClassName="order-summary__footer__label"
              iconOnLeftSrc="/src/assets/svg/lock.svg"
              iconOnRightSrc="/src/assets/svg/double_arrow.svg"
              disabled={!enablePlaceOrder}
              onClick={continueToPay}
            >
              Proceed to Pay
            </Button>
          </ToolTip>
          <div className={`${footerContentClassName}`}>
            <PaymentLogos
              className="order-summary-payment-logos"
              showVenmoLogo
              showChequeLogo={false}
            />
          </div>
        </div>
      )}

      {(isPayLater || !isBraintreeEnabled) && !isStoreCheckout && (
        <div className={`${styleClass}__footer checkout-footer`}>
          <ToolTip
            title={getTooltipTitle(
              priceZeroCartItem,
              enablePlaceOrder,
              isSummaryContainsRejectedItems
            )}
            overlayInnerStyle={{ boxShadow: '0px 4px 15px #8588AC26', color: '#E86868' }}
          >
            <Button
              className={`${styleClass}__footer__button`}
              iconClassName="order-summary__footer__icon"
              labelClassName="order-summary__footer__label"
              iconOnLeftSrc="/src/assets/svg/lock.svg"
              iconOnRightSrc="/src/assets/svg/double_arrow.svg"
              disabled={!enablePlaceOrder}
              onClick={onSubmit}
            >
              {isMobile ? REGULAR_ORDER_SUMMARY_TEXT_MOBILE : REGULAR_ORDER_SUMMARY_TEXT}
            </Button>
          </ToolTip>
          <div className={`${footerContentClassName}`}>
            <PaymentLogos
              className="order-summary-payment-logos"
              showVenmoLogo={hasAccount}
              showChequeLogo={hasAccount}
              showPaypalLogo={isBraintreeEnabled}
              showGooglePayLogo={isBraintreeEnabled}
            />
          </div>
        </div>
      )}

      {/* Store Checkout Buttons - Always visible at bottom */}
      {isStoreCheckout && (
        <div
          className={`${styleClass}__store-buttons-container`}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            marginTop: 'auto',
            paddingTop: '24px',
            width: '100%',
            boxSizing: 'border-box',
            flexShrink: 0,
          }}
        >
          {/* Confirm & Launch Store Button */}
          <ToolTip
            title={storeLaunchTooltipTitle}
            overlayInnerStyle={STORE_LAUNCH_DISABLED_TOOLTIP_INNER_STYLE}
          >
            <button
              type="button"
              className={`${styleClass}__store-confirm-button`}
              style={{
                cursor: enablePlaceOrder ? 'pointer' : 'not-allowed',
                opacity: enablePlaceOrder ? 1 : 0.5,
              }}
              onClick={onSubmit}
              disabled={!enablePlaceOrder}
            >
              <span className={`${styleClass}__store-button-label`}>
                {window.location.pathname.includes('/store/edit/')
                  ? 'Save Changes'
                  : 'Confirm & Launch Store'}
              </span>
              <span className={`${styleClass}__store-button-icon`}>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M7.5 15L12.5 10L7.5 5"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </button>
          </ToolTip>

          {/* Preview Store Button */}
          <button
            type="button"
            className={`${styleClass}__store-preview-button`}
            style={{
              cursor: canPreviewStore ? 'pointer' : 'not-allowed',
              opacity: canPreviewStore ? 1 : 0.5,
            }}
            onClick={onPreviewStore}
            disabled={!canPreviewStore}
          >
            <span className={`${styleClass}__store-button-label`}>Preview Store</span>
            <span className={`${styleClass}__store-button-icon`} style={{ fontSize: '18px' }}>
              👀
            </span>
          </button>
        </div>
      )}
    </div>
  );
};

export default OrderSummary;
